import { Input } from 'common/ui/input';
import React, { useEffect, useState } from 'react';

interface NumberInputProps extends Omit<React.ComponentProps<typeof Input>, 'onChange' | 'value'> {
  value?: number | undefined;
  onChange?: (value: number | undefined) => void;
  placeholder?: string;
  allowNegative?: boolean;
  precision?: number;
}

// Custom function to format numbers with thousand separators
const formatNumberWithSeparators = (value: number): string => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  ({ value, onChange, placeholder, allowNegative = false, precision, ...props }, ref) => {
    const [displayValue, setDisplayValue] = useState<string>('');

    // Update display value when prop value changes
    useEffect(() => {
      if (value === undefined || value === null || value === 0) {
        setDisplayValue('');
      } else {
        // Format the number with thousand separators
        const formatted = formatNumberWithSeparators(value);
        setDisplayValue(formatted);
      }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      
      // Remove all non-numeric characters except decimal point and minus sign
      let cleanedValue = inputValue.replace(/[^\d.-]/g, '');
      
      // Handle negative numbers
      if (!allowNegative) {
        cleanedValue = cleanedValue.replace(/-/g, '');
      }
      
      // Ensure only one decimal point
      const parts = cleanedValue.split('.');
      if (parts.length > 2) {
        cleanedValue = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Handle precision
      if (precision !== undefined && parts.length === 2 && parts[1].length > precision) {
        cleanedValue = parts[0] + '.' + parts[1].slice(0, precision);
      }
      
      // Update display value with formatting
      if (cleanedValue === '' || cleanedValue === '-') {
        setDisplayValue(cleanedValue);
        onChange?.(undefined);
        return;
      }
      
      // Parse the cleaned value to a number
      const numValue = parseFloat(cleanedValue);
      
      if (!isNaN(numValue)) {
        // Format for display with thousand separators
        const formatted = formatNumberWithSeparators(numValue);
        setDisplayValue(formatted);
        onChange?.(numValue);
      } else {
        setDisplayValue(cleanedValue);
        onChange?.(undefined);
      }
    };

    const handleBlur = () => {
      // On blur, ensure we have a valid number or clear the field
      if (displayValue === '' || displayValue === '-') {
        setDisplayValue('');
        onChange?.(undefined);
      } else {
        // Remove commas and parse
        const numValue = parseFloat(displayValue.replace(/,/g, ''));
        if (!isNaN(numValue)) {
          const formatted = formatNumberWithSeparators(numValue);
          setDisplayValue(formatted);
          onChange?.(numValue);
        } else {
          setDisplayValue('');
          onChange?.(undefined);
        }
      }
    };

    return (
      <Input
        ref={ref}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        {...props}
      />
    );
  }
);

NumberInput.displayName = 'NumberInput';

export default NumberInput; 